import { animate, inView, scroll } from "vendor";

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isMobileViewport = () =>
  window.matchMedia("(max-width: 699px)").matches;

function revealStagger(container, selector, options = {}) {
  const items = container.querySelectorAll(selector);
  if (!items.length) return;

  if (prefersReducedMotion()) {
    items.forEach((el) => el.classList.add("is-visible"));
    return;
  }

  inView(
    container,
    () => {
      items.forEach((el, i) => {
        setTimeout(() => el.classList.add("is-visible"), i * (options.delay || 120));
      });
    },
    { margin: "-80px" }
  );
}

class SwAboutHero extends HTMLElement {
  connectedCallback() {
    if (this.dataset.animations === "false") {
      this.querySelectorAll(".sw-about-hero__title-line").forEach((el) =>
        el.classList.add("is-visible")
      );
      return;
    }

    const media = this.querySelector(".sw-about-hero__media-inner");
    const lines = this.querySelectorAll(".sw-about-hero__title-line");

    if (!prefersReducedMotion() && media && !isMobileViewport()) {
      animate(media, { transform: ["scale(1.05)", "scale(1)"] }, { duration: 1.2, easing: "ease-out" });

      if (this.dataset.parallax !== "false") {
        const parallaxTarget = media.querySelector("img, video-media, picture");
        if (parallaxTarget) {
          scroll(
            animate(parallaxTarget, { transform: ["scale(1.06) translateY(-3%)", "scale(1.06) translateY(3%)"] }, { easing: "linear" }),
            { target: this, offset: ["start end", "end start"] }
          );
        }
      }
    } else if (media) {
      media.style.transform = "scale(1)";
    }

    lines.forEach((line, i) => {
      setTimeout(() => line.classList.add("is-visible"), prefersReducedMotion() ? 0 : 400 + i * 500);
    });

    if (prefersReducedMotion()) {
      const video = this.querySelector("video");
      if (video) video.pause();
    }
  }
}

class SwAboutReveal extends HTMLElement {
  connectedCallback() {
    const selector = this.dataset.selector || ".sw-about__reveal-item";

    if (window.Shopify?.designMode) {
      this.querySelectorAll(selector).forEach((el) => el.classList.add("is-visible"));
      return;
    }

    revealStagger(this, selector);
  }
}

class SwAboutParallax extends HTMLElement {
  connectedCallback() {
    if (prefersReducedMotion() || isMobileViewport() || this.dataset.animations === "false") return;

    this.querySelectorAll("[data-depth]").forEach((el) => {
      const depth = (parseFloat(el.dataset.depth) || 2) / 10;
      scroll(
        animate(el, { transform: [`translateY(${-depth * 60}px)`, `translateY(${depth * 60}px)`] }, { easing: "linear" }),
        { target: el, offset: ["start end", "end start"] }
      );
    });
  }
}

class SwAboutRoute extends HTMLElement {
  connectedCallback() {
    const path = this.querySelector("path");
    if (!path) return;

    if (prefersReducedMotion() || this.dataset.animations === "false") {
      this.classList.add("is-drawn");
      return;
    }

    inView(this, () => this.classList.add("is-drawn"), { margin: "-100px" });
  }
}

class SwAboutMosaic extends HTMLElement {
  connectedCallback() {
    revealStagger(this, ".sw-about-community__mosaic-item, .sw-about-manifesto__line", { delay: 100 });
  }
}

class SwAboutClosing extends HTMLElement {
  connectedCallback() {
    const content = this.querySelector(".sw-about-closing__content");
    if (!content) return;

    if (prefersReducedMotion() || this.dataset.animations === "false") {
      content.style.opacity = "1";
      return;
    }

    content.style.opacity = "0";
    content.style.transform = "translateY(1.5rem)";

    inView(
      this,
      () => {
        animate(content, { opacity: [0, 1], transform: ["translateY(1.5rem)", "translateY(0)"] }, { duration: 0.7, easing: "ease-out" });
      },
      { margin: "-100px" }
    );

    if (this.dataset.parallax !== "false" && !prefersReducedMotion() && !isMobileViewport()) {
      const img = this.querySelector(".sw-about-closing__media img");
      if (img) {
        scroll(
          animate(img, { transform: ["scale(1.05) translateY(-2%)", "scale(1.05) translateY(2%)"] }, { easing: "linear" }),
          { target: this, offset: ["start end", "end start"] }
        );
      }
    }
  }
}

class SwAboutProduct extends HTMLElement {
  connectedCallback() {
    revealStagger(this, ".sw-about-product__feature", { delay: 150 });
  }
}

class SwAboutDna extends HTMLElement {
  connectedCallback() {
    revealStagger(this, ".sw-about-dna__pillar", { delay: 150 });
  }
}

class SwAboutAudience extends HTMLElement {
  connectedCallback() {
    revealStagger(this, ".sw-about-audience__item", { delay: 100 });
  }
}

if (!window.__swAboutInit) {
  window.__swAboutInit = true;

  const components = [
    ["sw-about-hero", SwAboutHero],
    ["sw-about-reveal", SwAboutReveal],
    ["sw-about-parallax", SwAboutParallax],
    ["sw-about-route", SwAboutRoute],
    ["sw-about-mosaic", SwAboutMosaic],
    ["sw-about-closing", SwAboutClosing],
    ["sw-about-product", SwAboutProduct],
    ["sw-about-dna", SwAboutDna],
    ["sw-about-audience", SwAboutAudience],
  ];

  components.forEach(([name, Ctor]) => {
    if (!customElements.get(name)) customElements.define(name, Ctor);
  });
}
